/**
 * The sample product the editor preview renders.
 *
 * Build-time data, no personal data (NFR A4). Values are the prototype's own so
 * the preview reads identically side by side with it (spec R1).
 */

import { formatMoney } from '../i18n';

export interface PreviewProduct {
  sku: string;
  title: string;
  modelNumber: string;
  brand: string;
  shortDescription: string;
  unitOfMeasure: string;
  currency: string;
  listPrice: number;
  salePrice: number;
  pricePerUnit: number;
  savingsAmount: number;
  savingsPercent: number;
  inStock: boolean;
  stockLabel: string;
  leadTime: string;
  quantityAvailable: number;
  discontinued: boolean;
  rating: number;
  reviewCount: number;
  minimumOrderQuantity: number;
  quantityIncrement: number;
  preferred: boolean;
  badge: string;
  promotionalMessage: string;
  newArrival: boolean;
  bestSellerRank: number;
  sponsored: boolean;
  ctaLabel: string;
}

export const PREVIEW_PRODUCT: PreviewProduct = {
  sku: 'PRD-00123',
  title: 'Industrial Pressure Gauge 0-100 PSI',
  modelNumber: 'ASH-2271-100',
  brand: 'Ashcroft',
  shortDescription:
    'Durable steel gauge with IP65 rating for industrial use. Compatible with hydraulic and pneumatic systems. Threaded 1/4" NPT brass connection. Glycerin-filled for vibration damping.',
  unitOfMeasure: 'Each',
  currency: 'USD',
  listPrice: 58,
  salePrice: 42,
  pricePerUnit: 42,
  savingsAmount: 16,
  savingsPercent: 28,
  inStock: true,
  stockLabel: 'In Stock',
  leadTime: '2–3 business days',
  quantityAvailable: 847,
  discontinued: false,
  rating: 4.4,
  reviewCount: 128,
  minimumOrderQuantity: 1,
  quantityIncrement: 1,
  preferred: true,
  badge: 'BEST SELLER',
  promotionalMessage: 'Free shipping over $50',
  newArrival: false,
  bestSellerRank: 3,
  sponsored: false,
  ctaLabel: 'Add to Cart',
};

/**
 * A price, in the currency the product is actually denominated in.
 *
 * Was `` `$${amount.toFixed(2)}` `` — a hardcoded dollar sign on a product that
 * carries `currency: 'USD'` two fields above. That is not a formatting nit: a
 * product priced in EUR rendered `$42.00`, which misstates the price rather than
 * merely styling it oddly.
 *
 * en-US output is unchanged. `Intl.NumberFormat('en-US', {currency:'USD'})`
 * produces exactly `'$42.00'`, so the switch costs nothing at the pinned locale
 * — verified in `__tests__/i18n.test.ts` rather than assumed.
 */
export const formatPrice = (
  amount: number,
  currency: string = PREVIEW_PRODUCT.currency
): string => formatMoney(amount, currency);
